import React, { useMemo } from 'react';
import { AvatarGroup } from 'primereact/avatargroup';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';

import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { getIconClass } from 'modules/calendarv2/utils/eventColorhelper';
import ImageComponent from 'shared/ImageComponent';
import { getTenantDetails } from 'utils/utils';
import useWindowSize from 'hooks/useWindowSize';

const ListEventContent = ({ eventInfo, timezone }) => {
  const { event } = eventInfo;
  const { width } = useWindowSize();
  const tenantId = useMemo(() => getTenantDetails()[0], []);
  const extendedProps = event.extendedProps || {};
  const attendees = extendedProps.participants || [];
  const attendeeCount = Array.isArray(attendees) ? attendees.length : 0;

  // Check if we're on mobile/small screen (reactive to window resize)
  const isMobile = width < 768;

  const renderAttendeesAvatars = (isMobileView) => {
    const classPrefix = isMobileView ? 'mobile' : 'desktop';

    return (
      <AvatarGroup>
        {attendees.map((user, i) => {
          if (i < 4) {
            const stableId = `${i}-${user?.contact_id || user?.firm_user_id || i}`;
            return (
              <ImageComponent
                key={stableId}
                filePath={
                  user?.contact_id || user?.firm_user_id
                    ? `${tenantId}/${user?.contact_id ? user?.contact_id : user?.firm_user_id}/profile_image`
                    : null
                }
                fileName={user.name}
                className={`me-1 task-avatar assignee-${stableId}`}
                toolTipTarget={`.assignee-${stableId}`}
                toolTipContent={
                  <>
                    {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                    {user.name}
                  </>
                }
                tooltipPosition="top"
                showTooltip={true}
              />
            );
          }
          return null;
        })}
        {attendees.length > 4 && (
          <>
            <Avatar
              label={`+${attendees.length - 4}`}
              className={`me-1 pointer more-attendees-${classPrefix}-${event.id}`}
              shape="circle"
            />
            <Tooltip target={`.more-attendees-${classPrefix}-${event.id}`} position="top" className="case-tooltip mb-3">
              <div className="attendees-tooltip-list">
                {attendees.slice(4).map((user, idx) => (
                  <div key={idx} className="attendees-tooltip-item">
                    {user?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                    {user.name}
                  </div>
                ))}
              </div>
            </Tooltip>
          </>
        )}
      </AvatarGroup>
    );
  };

  const getEventTitle = () => {
    if (extendedProps.calendar_type === 'case') {
      return 'Statute Of Limitations';
    }
    if (extendedProps.calendar_type === 'user') {
      return 'Birthday: ' + extendedProps.contact_name;
    }
    return event.title;
  };

  if (isMobile) {
    // Mobile card layout
    return (
      <div className="fc-list-event-mobile-card">
        <div className="fc-list-event-card-header">
          <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
          <div className="fc-list-event-card-title">
            <i
              className={`${getIconClass(extendedProps.calendar_type, extendedProps.is_google_event)} me-2`}
              style={{ color: 'var(--black-500)' }}
            ></i>
            {getEventTitle()}
          </div>
          <div className="fc-list-event-card-time">{event.allDay ? 'All Day' : `${eventInfo.timeText}`}</div>
        </div>
        <div className="fc-list-event-card-content">
          <div className="fc-list-event-card-row">
            <div className="fc-list-event-card-label">Case/Lead:</div>
            <div className="fc-list-event-card-value">{extendedProps.case_name || 'N/A'}</div>
          </div>
          {extendedProps.practiceArea && (
            <div className="fc-list-event-card-row">
              <div className="fc-list-event-card-label">Practice Area:</div>
              <div className="fc-list-event-card-value">{extendedProps.practiceArea}</div>
            </div>
          )}
          {extendedProps.event_type && (
            <div className="fc-list-event-card-row">
              <div className="fc-list-event-card-label">Type:</div>
              <div className="fc-list-event-card-value">{extendedProps.event_type}</div>
            </div>
          )}
          {extendedProps.due_date && (
            <div className="fc-list-event-card-row">
              <div className="fc-list-event-card-label">Due Date:</div>
              <div className="fc-list-event-card-value">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</div>
            </div>
          )}
          {extendedProps.room_name && (
            <div className="fc-list-event-card-row">
              <div className="fc-list-event-card-label">Room:</div>
              <div className="fc-list-event-card-value">{extendedProps.room_name}</div>
            </div>
          )}
          <div className="fc-list-event-card-row">
            <div className="fc-list-event-card-label">Attendees:</div>
            <div className="fc-list-event-card-value">
              {attendeeCount > 0 ? (
                <>
                  <div className="attendees-count-mobile">
                    {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                  </div>
                  <div className="attendees-names-mobile">{renderAttendeesAvatars(true)}</div>
                </>
              ) : (
                <div>No attendees</div>
              )}
            </div>
          </div>
          <div className="fc-list-event-card-row">
            <div className="fc-list-event-card-label">Location:</div>
            <div className="fc-list-event-card-value">{extendedProps.location || 'N/A'}</div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop table layout
  return (
    <div className="fc-list-event-custom-wrapper">
      <div className="fc-list-event-custom-cell event-cell">
        <div className="fc-list-event-indicator" style={{ backgroundColor: event.backgroundColor || '#517dda' }}></div>
        <div className="fc-list-event-details">
          <div className="fc-list-event-title">
            <i
              className={`${getIconClass(extendedProps.calendar_type, extendedProps.is_google_event)} me-2`}
              style={{ color: 'var(--black-500)' }}
            ></i>
            {getEventTitle()}
          </div>
          <div className="d-flex align-items-center flex-wrap gap-3">
            {extendedProps.case_name && (
              <div className="fc-list-event-subtitle">
                Case/Lead: <span className="text-bold">{extendedProps.case_name}</span>
              </div>
            )}
            {extendedProps.practiceArea && (
              <div className="fc-list-event-subtitle">
                Practice area: <span className="text-bold">{extendedProps.practiceArea}</span>
              </div>
            )}
            {extendedProps.event_type && (
              <div className="fc-list-event-subtitle">
                Type: <span className="text-bold">{extendedProps.event_type}</span>
              </div>
            )}
            {extendedProps.due_date && (
              <div className="fc-list-event-subtitle">
                Due date: <span className="text-bold">{convertDateFromApiToUserTimezone(extendedProps.due_date, timezone)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="fc-list-event-custom-cell attendees-cell">
        {attendeeCount > 0 ? (
          <>
            <div className="attendees-count">
              {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
            </div>
            <div className="attendees-names">{renderAttendeesAvatars(false)}</div>
          </>
        ) : (
          <div className="attendees-names">No attendees</div>
        )}
      </div>
      <div className="fc-list-event-custom-cell location-cell">Location: {extendedProps.location || 'N/A'}</div>
    </div>
  );
};

export default ListEventContent;
